/* Outbound transitions — real to-device knock / grab, and follow.
   No mock simulation: if the target isn't a real MXID, nothing is sent.
   Inbound delivery + toasts live in the notify store.
   (Whisper was removed: it sent a canned "wants a quick word" with no reply
   path — confusing and redundant next to knock. Chat + knock cover it.)

   Follow is an attachment, not a one-shot jump: while following someone you
   move with them into every room they enter. Team rooms you can't walk into
   trigger the protocol knock instead of an instant entry (the regular
   enterRoom flow handles that). Navigating somewhere else yourself detaches. */
import { watch } from 'vue'
import { defineStore } from 'pinia'
import { matrix } from '@/lib/matrix'
import { router } from '@/router'
import { useToasts } from '@/stores/toasts'
import { useFloor } from '@/stores/floor'
import { localpart } from '@/lib/util'
import type { Person } from '@/types/atrium'

const isMx = (id: string | undefined): id is string => typeof id === 'string' && id[0] === '@'
const isDnd = (mode: string) => mode === 'deep' || mode === 'break'

function toDevice(type: string, mxid: string, content: Record<string, unknown>): Promise<void> {
  return matrix.sendToDevice(type, { [mxid]: { '*': content } })
}

interface TransitionsState {
  followingId: string | null
  followingName: string
  followMoving: boolean
  inited: boolean
}

export const useTransitions = defineStore('transitions', {
  state: (): TransitionsState => ({
    followingId: null,
    followingName: '',
    followMoving: false,
    inited: false,
  }),

  actions: {
    /** Wire the follow watchers once (called from bootstrap). */
    init() {
      if (this.inited) return
      this.inited = true
      const floor = useFloor()

      // The person we follow moved → move with them.
      watch(
        () => {
          if (!this.followingId) return null
          return floor.rooms.find((r) => r.occupants.includes(this.followingId!))?.id ?? null
        },
        (slug) => {
          if (this.followingId && slug && slug !== floor.activeSlug) void this.moveWithFollowed(slug)
        },
      )

      // We moved somewhere on our own → detach.
      watch(
        () => floor.activeSlug,
        (slug) => {
          if (!this.followingId || this.followMoving || !slug) return
          const theirs = floor.rooms.find((r) => r.occupants.includes(this.followingId!))?.id
          if (theirs && slug !== theirs) this.stopFollowing('You went your own way.')
        },
      )
    },

    me() {
      const id = matrix.userId() || ''
      // Always send a real name (display name → localpart), never 'Someone'.
      return { id, name: useFloor().you?.name || localpart(id) || 'Someone' }
    },

    async knock(person: Person, msg: string) {
      const toasts = useToasts()
      if (!isMx(person.id)) return
      const dnd = isDnd(person.mode)
      const me = this.me()
      try {
        await toDevice('app.atrium.knock', person.id, { from: me.id, name: me.name, msg, ts: Date.now() })
        toasts.push({
          icon: dnd ? 'moon' : 'knock',
          color: dnd ? 'var(--p-deep)' : undefined,
          title: dnd ? `Queued for ${person.short}` : `Knocking on ${person.short}…`,
          text: dnd ? 'They’ll see it when they’re free.' : 'waiting for a reply',
          ms: 5000,
        })
      } catch (e) {
        toasts.push({ icon: 'x', color: 'var(--danger)', title: 'Knock failed to send', text: String((e as Error).message || e) })
      }
    },

    async grab(person: Person) {
      const toasts = useToasts()
      const floor = useFloor()
      if (!isMx(person.id)) return
      const slug = floor.activeSlug
      if (!slug) {
        toasts.push({ icon: 'grab', title: 'Enter a room first', text: 'Grab pulls someone into the room you’re in.' })
        return
      }
      const room = floor.roomBySlug(slug)
      const me = this.me()
      try {
        await toDevice('app.atrium.grab', person.id, { from: me.id, name: me.name, slug, roomName: room?.name || 'a room', ts: Date.now() })
        toasts.push({ icon: 'grab', title: `Pulling ${person.short} in`, text: `to ${room?.name || 'your room'}` })
      } catch (e) {
        toasts.push({ icon: 'x', color: 'var(--danger)', title: 'Grab failed to send', text: String((e as Error).message || e) })
      }
    },

    /** Toggle following: attach to a person and move with them room to room. */
    follow(person: Person) {
      if (this.followingId === person.id) {
        this.stopFollowing()
        return
      }
      const floor = useFloor()
      this.followingId = person.id
      this.followingName = person.short
      const room = floor.rooms.find((r) => r.occupants.includes(person.id))
      useToasts().push({
        icon: 'arrowR',
        title: `Following ${person.short}`,
        text: room ? `into ${room.name} — you'll move when they do.` : 'You’ll move with them when they enter a room.',
        action: 'Stop',
        onAction: () => this.stopFollowing(),
        ms: 6000,
      })
      if (room) void this.moveWithFollowed(room.id)
    },

    stopFollowing(reason?: string) {
      if (!this.followingId) return
      const name = this.followingName
      this.followingId = null
      this.followingName = ''
      useToasts().push({ icon: 'arrowR', title: `Stopped following ${name}`, text: reason, ms: 4000 })
    },

    /** Move with the followed person; team rooms knock via the entry flow. */
    async moveWithFollowed(slug: string) {
      const floor = useFloor()
      if (floor.activeSlug === slug) return
      const room = floor.roomBySlug(slug)
      this.followMoving = true
      try {
        if (room && !floor.canWalkIn(room)) {
          useToasts().push({
            icon: 'knock', color: room.accent,
            title: `${this.followingName} went into ${room.name}`,
            text: 'Team room — knocking for you.', ms: 5000,
          })
        }
        await router.push(`/room/${slug}`)
      } finally {
        // Let the route watchers settle before manual-move detection resumes.
        setTimeout(() => { this.followMoving = false }, 400)
      }
    },
  },
})
